/**
 * `useDataTablePropValidation` — the preset's prop-contract guards, in one place: the two
 * combinations that would MIS-RENDER (fail loud) and the three that merely become no-ops or
 * silently ignore an input (dev-warn, tree-shaken from prod). Renders nothing and returns
 * nothing; it exists so `data-table.tsx` orchestrates and every concern lives in a co-located
 * hook.
 *
 * Called AFTER `useDataTable` because the grow-column check reads engine state
 * (`getIsPinned`/`getCanResize`) that only exists once the table is built. The throws still run
 * before this hook's own effects, exactly as they did inline.
 */
import { useEffect } from 'react'
import type { PaginationState, RowData, RowSelectionState, Table } from '@tanstack/react-table'

import type { DataTableDetailPanel, DataTableSelection } from '../data-table.types'
import { readColumnMeta } from './column-meta'

export type UseDataTablePropValidationOptions<TData extends RowData> = {
  table: Table<TData>
  /** The infinite tier is engaged (`infinite !== undefined`). */
  isInfinite: boolean
  /** Rows are windowed — the infinite tier, or the client tier's `virtualizeRows` opt-in. */
  isWindowed: boolean
  pagination: PaginationState | undefined
  detailPanel: DataTableDetailPanel<TData> | undefined
  selection: DataTableSelection | undefined
  rowSelection: RowSelectionState | undefined
  onRowSelectionChange: unknown
  virtualizeRows: boolean | undefined
  height: number | string | undefined
  maxHeight: number | string | undefined
}

export const useDataTablePropValidation = <TData extends RowData>({
  table,
  isInfinite,
  isWindowed,
  pagination,
  detailPanel,
  selection,
  rowSelection,
  onRowSelectionChange,
  virtualizeRows,
  height,
  maxHeight,
}: UseDataTablePropValidationOptions<TData>): void => {
  // `pagination` and `infinite` drive mutually exclusive row models — client/server paging
  // slices to one page, the infinite tier lays out the full-server-total windowed slots — that
  // share the engine's single row model. Supplying both silently mis-renders (the pagination
  // slice shrinks `rows` while the virtualizer lays out the loaded rows plus its placeholder
  // lookahead, so nearly
  // every windowed slot resolves to an unloaded placeholder). The type documents them as
  // exclusive but can't express it without collapsing the composable-props API, so fail loud.
  if (isInfinite && pagination !== undefined) {
    throw new Error(
      'DataTable: `pagination` and `infinite` are mutually exclusive — pass one tier, not both.',
    )
  }

  // Windowed slots are laid out at fixed per-density heights (`estimateSize`), so a
  // content-sized detail panel would overlap the rows below it — a silent mis-render,
  // failed loud like the tier conflict above.
  if (detailPanel !== undefined && isWindowed) {
    throw new Error(
      'DataTable: `detailPanel` cannot be combined with `infinite`/`virtualizeRows` — windowed slots have fixed heights, so a content-sized panel would overlap the rows below it.',
    )
  }

  // `selection` and the raw `rowSelection`/`onRowSelectionChange` props share the engine's
  // single selection slot, and the model derived from `selection` wins — so a raw prop passed
  // alongside `selection` is silently ignored. Unlike the `pagination`/`infinite` pair this
  // doesn't mis-render, so a raw prop is a caller mistake worth surfacing, not a fail-loud
  // break: warn (dev-only, tree-shaken from prod) instead of throwing.
  const hasSelectionSlotConflict =
    selection !== undefined && (rowSelection !== undefined || onRowSelectionChange !== undefined)
  useEffect(
    function warnOnConflictingSelectionProps() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- dev-only prop-conflict diagnostic, not an event: no user interaction triggers it, it's render-time prop validation. Warning during render would fire on every render while the conflict persists and double under StrictMode; the effect's [hasSelectionSlotConflict] dep dedupes it to once per conflict-appearance.
      if (import.meta.env.DEV && hasSelectionSlotConflict) {
        console.warn(
          'DataTable: `selection` and the raw `rowSelection`/`onRowSelectionChange` props are mutually exclusive — `selection` wins and the raw prop is ignored. Pass one selection API, not both.',
        )
      }
    },
    [hasSelectionSlotConflict],
  )

  // `virtualizeRows` windows only against a bounded scroll viewport; with neither bound the
  // container grows to content, every row still mounts, and the opt-in silently becomes a no-op.
  // Like the selection conflict above, this doesn't mis-render — so dev-warn (tree-shaken from
  // prod) rather than throw. A `height` of any shape counts as provided (a number, or a CSS
  // string like "100%" a sized flex parent bounds); only omitting BOTH is the footgun.
  // Either bound gives the virtualizer a viewport, so only "neither" is the footgun.
  const hasNoBoundedHeight = height === undefined && maxHeight === undefined
  useEffect(
    function warnOnVirtualizeRowsWithoutHeight() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- dev-only prop-conflict diagnostic, not an event: no user interaction triggers it, it's render-time prop validation. Warning during render would fire on every render while the misconfig persists and double under StrictMode; the effect's [virtualizeRows, hasNoBoundedHeight] deps dedupe it to once per misconfig-appearance.
      if (import.meta.env.DEV && virtualizeRows === true && hasNoBoundedHeight) {
        console.warn(
          'DataTable: `virtualizeRows` needs a bounded `height` or `maxHeight` (a number, or a CSS length like "100%" inside a sized flex parent) to window rows — without one every row still mounts and the virtualization is a no-op.',
        )
      }
    },
    [virtualizeRows, hasNoBoundedHeight],
  )

  // A `meta.grow` column absorbs the row's spare width (`flex: 1 0 size`), so pinning it or
  // leaving it resizable fights the auto-fill — contradictions the type can't encode. It fails
  // quietly rather than mis-rendering loudly, so dev-warn (tree-shaken from prod) rather than throw.
  // `getCanResize()` folds in the table-level resizing switch, so the resize half only fires when
  // a handle would actually render.
  const hasGrowColumnConflict =
    import.meta.env.DEV &&
    table
      .getAllColumns()
      .some(
        (column) =>
          readColumnMeta(column)?.grow === true &&
          (column.getIsPinned() !== false || column.getCanResize()),
      )
  useEffect(
    function warnOnGrowColumnConflict() {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- dev-only prop-conflict diagnostic, not an event: no user interaction triggers it, it's render-time prop validation. Warning during render would fire on every render while the conflict persists and double under StrictMode; the effect's [hasGrowColumnConflict] dep dedupes it to once per conflict-appearance.
      if (import.meta.env.DEV && hasGrowColumnConflict) {
        console.warn(
          'DataTable: a `meta.grow` column must not be pinned or resizable — it fills the row’s spare width, so a sticky pin or a resize drag fights the auto-fill. Pair `grow` with `enableResizing: false` and leave the column unpinned.',
        )
      }
    },
    [hasGrowColumnConflict],
  )
}
